/**
 * AI Recruit - Resume Upload Modal
 * Modal component for uploading resumes with drag & drop support
 */

import React, { useState, useCallback, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { 
  CloudArrowUpIcon, 
  XMarkIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

import { resumesApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import DuplicateDetectionModal, { type DuplicateDetectionData } from './DuplicateDetectionModal'
import SourceSelector from '@/components/common/SourceSelector'

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose }) => {
  const [dragOver, setDragOver] = useState(false)
  const [uploadingFile, setUploadingFile] = useState<string | null>(null)
  const [duplicateData, setDuplicateData] = useState<DuplicateDetectionData | null>(null)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [blacklistedIds, setBlacklistedIds] = useState<string[]>([])
  const [uploadedResumeId, setUploadedResumeId] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [source_id, setSourceId] = useState<string>('')

  const queryClient = useQueryClient()

  // Reset transient state (including the selected source) whenever the modal opens,
  // so a previously selected source/file does not persist into the next upload.
  useEffect(() => {
    if (isOpen) {
      setSourceId('')
      setPendingFile(null)
      setUploadingFile(null)
      setDuplicateData(null)
      setShowDuplicateModal(false)
      setBlacklistedIds([])
      setIsProcessing(false)
    }
  }, [isOpen])

  // Duplicate detection mutation
  const duplicateCheckMutation = useMutation({
    mutationFn: (formData: FormData) => resumesApi.searchDuplicates(formData),
    onSuccess: (response) => {
      console.log('🔍 Duplicate check response:', response)
      const data = response.data
      console.log('🔍 Duplicate check data:', data)
      console.log('🔍 Total found:', data.total_found)
      if (data.total_found > 0) {
        // Show duplicate detection modal
        setDuplicateData({
          has_duplicates: true,
          extracted_info: {
            candidate_name: data.search_criteria.name || null,
            candidate_email: data.search_criteria.email || null,
            candidate_phone: data.search_criteria.phone || null,
            file_name: pendingFile?.name || 'Unknown',
            file_size: pendingFile?.size || 0
          },
          duplicate_candidates: data.results,
          total_duplicates_found: data.total_found,
          popup_config: {
            title: "Potential Duplicate Resumes Found",
            message: `Found ${data.total_found} potential duplicate(s) for this candidate.`,
            show_upload_button: true,
            upload_endpoint: "/api/v2/resumes/upload",  // Changed to V2 for Foundry integration
            cancel_text: "Cancel Upload",
            upload_text: "Upload Anyway"
          }
        })
        setShowDuplicateModal(true)
      } else {
        // No duplicates found, proceed with normal upload
        console.log('ℹ️ No duplicates found, proceeding with upload')
        setIsProcessing(true)
        proceedWithUpload()
      }
      setUploadingFile(null)
    },
    onError: (error: any) => {
      console.error('Duplicate check failed:', error)
      console.error('Error response:', error.response)
      console.error('Error message:', error.message)
      // If duplicate check fails, proceed with normal upload
      toast.error('Duplicate check failed, proceeding with upload...')
      setIsProcessing(true)
      proceedWithUpload()
      setUploadingFile(null)
    }
  })

  // Blacklist mutation
  const blacklistMutation = useMutation({
    mutationFn: ({ blacklistedIds }: { blacklistedIds: string[] }) => {
      console.log('🚫 Blacklist mutation function called with:', { blacklistedIds })
      return resumesApi.blacklistDuplicates({ blacklisted_resume_ids: blacklistedIds })
    },
    onSuccess: (response) => {
      console.log('✅ Blacklisted duplicates:', response)
      const data = response.data
      toast.success(`Blacklisted ${data.blacklisted_count} duplicate resumes`)
    },
    onError: (error: any) => {
      console.error('❌ Failed to blacklist duplicates:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error('Failed to blacklist duplicates')
    }
  })

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => {
      console.log('📤 Upload mutation function called')
      return resumesApi.upload(formData)
    },
    onSuccess: (data) => {
      console.log('✅ Upload successful, closing modal')
      setUploadedResumeId(data.data.id)
      toast.success(`Resume "${data.data.filename}" uploaded successfully!`)
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      setUploadingFile(null)
      setDuplicateData(null)
      setShowDuplicateModal(false)
      setPendingFile(null)
      setIsProcessing(false)
      
      // If there are blacklisted IDs, call the blacklist API
      if (blacklistedIds.length > 0) {
        console.log('🚫 Calling blacklist API with:', { resumeId: data.data.id, blacklistedIds })
        console.log('🚫 Blacklist mutation starting...')
        blacklistMutation.mutate({
          blacklistedIds: blacklistedIds
        })
        setBlacklistedIds([]) // Clear after use
      } else {
        console.log('ℹ️ No blacklisted IDs to process')
      }
      
      console.log('🚪 Calling onClose to close modal')
      onClose() // Close modal after successful upload
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Upload failed'
      toast.error(message)
      setUploadingFile(null)
    },
    onSettled: (data, error, formData) => {
      // Reset uploading state
      setUploadingFile(null)
    }
  })

  // Helper function to proceed with upload
  const proceedWithUpload = useCallback(() => {
    if (!pendingFile) {
      console.log('❌ No pending file, cannot proceed with upload')
      return
    }
    
    console.log('📤 Starting upload process for file:', pendingFile.name)
    const formData = new FormData()
    formData.append('file', pendingFile)
    if (source_id) {
      formData.append('source_id', source_id)
    }
    setUploadingFile(pendingFile.name)
    console.log('📤 Calling upload mutation...')
    uploadMutation.mutate(formData)
  }, [pendingFile, source_id, uploadMutation])

  // Handle upload anyway from duplicate modal
  const handleUploadAnyway = useCallback(() => {
    setShowDuplicateModal(false)
    proceedWithUpload()
  }, [proceedWithUpload])

  // Handle cancel from duplicate modal
  const handleCancelUpload = useCallback(() => {
    setShowDuplicateModal(false)
    setDuplicateData(null)
    setPendingFile(null)
    setUploadingFile(null)
    setBlacklistedIds([]) // Clear blacklisted IDs on cancel
    
    // Refresh resume list when canceling duplicate modal
    console.log('🔄 Refreshing resume list after canceling duplicate modal')
    queryClient.invalidateQueries({ queryKey: ['resumes'] })
    queryClient.invalidateQueries({ queryKey: ['resumes-for-matching'] })
    queryClient.invalidateQueries({ queryKey: ['matching-results'] })
    
    onClose() // Close the main upload modal as well
  }, [onClose, queryClient])

  // Handle blacklist changes from duplicate modal
  const handleBlacklistChange = useCallback((blacklistedIds: string[]) => {
    console.log('📝 Blacklist IDs updated in UploadModal:', blacklistedIds)
    setBlacklistedIds(blacklistedIds)
  }, [])

  // Blacklist function is no longer needed - blacklisting happens after upload

  // File upload handling - single file only
  const handleFileUpload = useCallback(async (files: FileList) => {
    const allowedTypes = ['pdf', 'doc', 'docx', 'txt']
    const maxSize = 10 * 1024 * 1024 // 10MB

    // Only process the first file if multiple are selected
    const file = Array.from(files)[0]
    
    if (!file) return

    // Validate source selection
    if (!source_id) {
      toast.error('Please select a source before uploading')
      return
    }

    // Validate file type
    const fileExt = file.name.split('.').pop()?.toLowerCase()
    if (!fileExt || !allowedTypes.includes(fileExt)) {
      toast.error(`File "${file.name}" has unsupported format. Allowed: ${allowedTypes.join(', ')}`)
      return
    }

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`File "${file.name}" exceeds 10MB limit`)
      return
    }

    // Store file for potential upload
    setPendingFile(file)
    setUploadingFile(file.name)

    // Create form data for duplicate check
    const formData = new FormData()
    formData.append('file', file)
    
    // First check for duplicates
    duplicateCheckMutation.mutate(formData)
  }, [source_id, duplicateCheckMutation])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileUpload(files)
    }
  }, [handleFileUpload])

  // File input handler
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      handleFileUpload(files)
    }
    e.target.value = '' // Reset input
  }, [handleFileUpload])

  // Close modal handler
  const handleClose = useCallback(() => {
    if (!uploadingFile && !duplicateCheckMutation.isPending && !isProcessing) {
      setDuplicateData(null)
      setShowDuplicateModal(false)
      setPendingFile(null)
      setIsProcessing(false)
      onClose()
    }
  }, [uploadingFile, duplicateCheckMutation.isPending, isProcessing, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Upload Resume
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={!!uploadingFile}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Source Selector */}
          <div className="mb-4">
            <SourceSelector
              type="resume"
              value={source_id}
              onChange={setSourceId}
              required={true}
            />
          </div>

          {/* Upload Area */}
          <div 
            className={`border-2 border-dashed rounded-lg p-8 transition-all duration-200 ${
              dragOver 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <label htmlFor="modal-resume-upload" className="cursor-pointer">
                  <span className="text-lg font-medium text-gray-900">
                    Drop resume here or <span className="text-blue-600 hover:text-blue-500">browse file</span>
                  </span>
                  <input
                    id="modal-resume-upload"
                    type="file"
                    className="sr-only"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileSelect}
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  PDF, DOC, DOCX, TXT up to 10MB
                </p>
              </div>
            </div>
          </div>

          {/* Uploading File Progress */}
          {(uploadingFile || duplicateCheckMutation.isPending || isProcessing) && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                {duplicateCheckMutation.isPending 
                  ? 'Checking for Duplicates...' 
                  : isProcessing 
                    ? 'Processing...' 
                    : 'Uploading Resume'
                }
              </h4>
              <div className="flex items-center space-x-2">
                <LoadingSpinner size="sm" />
                <span className="text-sm text-blue-700">
                  {isProcessing ? 'Uploading resume...' : uploadingFile}
                </span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={!!uploadingFile || duplicateCheckMutation.isPending || isProcessing}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {(uploadingFile || duplicateCheckMutation.isPending || isProcessing) ? 'Processing...' : 'Close'}
            </button>
          </div>
        </div>
      </div>

      {/* Duplicate Detection Modal */}
      <DuplicateDetectionModal
        isOpen={showDuplicateModal}
        onClose={handleCancelUpload}
        onUploadAnyway={handleUploadAnyway}
        duplicateData={duplicateData}
        isUploading={uploadMutation.isPending}
        onBlacklistChange={handleBlacklistChange}
      />
    </div>
  )
}

export default UploadModal
